
import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { BASE_URL } from "../../../config";
import "./EngineerAttendance.css";

/**
 * ─────────────────────────────────────────────────────────────────────────
 * ASSUMPTIONS (adjust to match your actual backend/auth shape):
 *
 * 1. localStorage "NagpurProperties" holds:
 *      { token, id, name, email, phoneNo, role: [{ roleName, roleDescription }], ... }
 *    `role` is an ARRAY of role objects (a user can hold more than one), so
 *    membership is checked via `getRoleNames(user).includes("ENGINEER")` etc.
 *    Adjust the role-name strings in `EngineerAttendance()` if your backend
 *    uses different values (e.g. "SuperEngineer" vs "Super Engineer").
 *
 * 2. Endpoint paths are used as `${BASE_URL}/engineer/...` (no "/api"
 *    prefix), matching your BASE_URL configuration.
 *
 * 3. There is no dedicated "am I already checked in today" GET endpoint, so
 *    today's check-in/check-out flow state is cached in localStorage per
 *    engineer+date (see readFlow/writeFlow). This is only a client-side
 *    convenience so a page refresh doesn't lose the button state within the
 *    same day/browser — it is NOT a source of truth across devices. If you
 *    add a "today status" endpoint later, swap the localStorage read in
 *    CheckInOutCard's useEffect for a real fetch.
 *
 * 4. Reverse geocoding (turning lat/lng into a readable address) tries
 *    `window.google.maps.Geocoder` first (if the Google Maps JS API happens
 *    to be loaded on this page already), then falls back to the free
 *    OpenStreetMap Nominatim reverse-geocoding API — no API key needed, one
 *    request per check-in/check-out. Either way the Address field stays
 *    editable, and the submit button is disabled until it's non-empty, so
 *    a failed lookup just means the person types the address in by hand.
 *
 * 5. Check-in request body (`engineerAttendanceRequest`) matches the
 *    confirmed schema exactly: `{ latitude, longitude, address,
 *    manualBikeReading }`. No `engineerId` is sent — the schema doesn't
 *    include one, so the backend must resolve the engineer from the auth
 *    token instead. The check-out body (`checkOutRequest`) uses the same
 *    shape by assumption, since its schema wasn't separately confirmed —
 *    if check-out still 400s on a missing engineerId, that's the first
 *    thing to check.
 *
 * 6. The multipart POSTs pass `"Content-Type": undefined` in the per-request
 *    headers. This isn't a no-op: your `axiosInstance` sets a default
 *    Content-Type (almost certainly "application/json") on every request,
 *    and simply omitting the header here does NOT clear that default —
 *    axios still sends it, which mislabels the multipart body and breaks
 *    boundary detection server-side ("Failed to parse multipart servlet
 *    request"). Explicitly setting the header to `undefined` tells axios to
 *    drop it for this call, so the browser can generate the correct
 *    `multipart/form-data; boundary=...` header itself. If you have direct
 *    access to axiosInstance.js, the cleaner long-term fix is to make sure
 *    it doesn't force a default Content-Type when the request body is a
 *    FormData instance.
 * ─────────────────────────────────────────────────────────────────────────
 */

// ─── Helpers ─────────────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, "0");
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("NagpurProperties")) || {};
  } catch {
    return {};
  }
}

function getRoleNames(user) {
  const roles = Array.isArray(user?.role) ? user.role : [];
  return roles.map((r) => (r?.roleName || "").trim().toUpperCase());
}

function resolvePhotoUrl(path) {
  if (!path) return null;
  return /^https?:\/\//i.test(path) ? path : `${BASE_URL}/${path.replace(/^\//, "")}`;
}

function flowKey(engineerId, dateStr) {
  return `attendanceFlow_${engineerId}_${dateStr}`;
}
function readFlow(engineerId, dateStr) {
  try {
    return JSON.parse(localStorage.getItem(flowKey(engineerId, dateStr)));
  } catch {
    return null;
  }
}
function writeFlow(engineerId, dateStr, value) {
  localStorage.setItem(flowKey(engineerId, dateStr), JSON.stringify(value));
}

function getCurrentLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this device/browser."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      (err) => reject(new Error(err.message || "Could not fetch your location.")),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
}

async function reverseGeocode(lat, lng) {
  // Prefer Google Maps JS API if it's already loaded on this page.
  if (window.google?.maps?.Geocoder) {
    try {
      const geocoder = new window.google.maps.Geocoder();
      const address = await new Promise((resolve) => {
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === "OK" && results?.[0]) resolve(results[0].formatted_address);
          else resolve("");
        });
      });
      if (address) return address;
    } catch {
      // fall through to the Nominatim fallback below
    }
  }

  // Fallback: OpenStreetMap Nominatim — free, no API key required.
  // Note: this calls a third-party domain (nominatim.openstreetmap.org) directly
  // from the browser. Nominatim's usage policy asks for light/non-bulk use;
  // fine for one lookup per check-in/check-out, but swap in Google's
  // Geocoding API (or your backend) if you need higher volume/reliability.
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`,
      { headers: { Accept: "application/json" } }
    );
    if (response.ok) {
      const json = await response.json();
      if (json?.display_name) return json.display_name;
    }
  } catch {
    // network/CORS failure — leave address blank so the person can type it in
  }
  return "";
}

const fmtTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—";

function extractErrorMessage(error) {
  const data = error?.response?.data;
  if (!data) return error?.message || "Something went wrong. Please try again.";
  if (typeof data === "string") return data;
  // Common Spring Boot validation shapes: { message }, { errors: [...] }, { errors: {field: msg} }
  const parts = [];
  if (data.message) parts.push(data.message);
  if (Array.isArray(data.errors)) {
    data.errors.forEach((e) => parts.push(typeof e === "string" ? e : e.defaultMessage || JSON.stringify(e)));
  } else if (data.errors && typeof data.errors === "object") {
    Object.entries(data.errors).forEach(([field, msg]) => parts.push(`${field}: ${msg}`));
  }
  return parts.length ? parts.join("\n") : `Request failed (${error?.response?.status || "unknown"})`;
}

// ─── Badges ──────────────────────────────────────────────────────────────
function StatusBadge({ status, small }) {
  const map = {
    PRESENT: { cls: "att-badge-present", label: "Present" },
    ABSENT: { cls: "att-badge-absent", label: "Absent" },
    HALF_DAY: { cls: "att-badge-halfday", label: "Half Day" },
    LATE: { cls: "att-badge-late", label: "Late" },
    LEAVE: { cls: "att-badge-leave", label: "Leave" },
    HOLIDAY: { cls: "att-badge-holiday", label: "Holiday" },
    WEEK_OFF: { cls: "att-badge-holiday", label: "Week Off" },
  };
  const entry = map[status] || {
    cls: "att-badge-default",
    label: status ? status.replace(/_/g, " ") : "—",
  };
  return <span className={`att-badge ${entry.cls} ${small ? "att-badge-sm" : ""}`}>{entry.label}</span>;
}

function LocationBadge({ status }) {
  if (!status) return null;
  const familiar = status === "FAMILIAR";
  return (
    <span className={`att-badge att-badge-sm ${familiar ? "att-badge-present" : "att-badge-late"}`}>
      {familiar ? "Familiar" : "Unfamiliar"}
    </span>
  );
}

// ─── Check-in / Check-out capture modal ─────────────────────────────────
function AttendanceCaptureModal({ mode, onClose, onSubmit, submitting }) {
  const isCheckIn = mode === "checkin";
  const [locating, setLocating] = useState(true);
  const [locationError, setLocationError] = useState("");
  const [coords, setCoords] = useState(null);
  const [address, setAddress] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [manualReading, setManualReading] = useState("");

  const captureLocation = useCallback(async () => {
    setLocating(true);
    setLocationError("");
    try {
      const pos = await getCurrentLocation();
      setCoords(pos);
      const addr = await reverseGeocode(pos.latitude, pos.longitude);
      setAddress(addr);
    } catch (err) {
      setLocationError(err.message || "Could not fetch location.");
    } finally {
      setLocating(false);
    }
  }, []);

  useEffect(() => {
    captureLocation();
  }, [captureLocation]);

  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const hasReading = manualReading !== "" && !Number.isNaN(parseFloat(manualReading));
  const canSubmit =
    !!coords && !!photoFile && address.trim() !== "" && hasReading && !locating && !submitting;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      latitude: coords.latitude,
      longitude: coords.longitude,
      address: address.trim(),
      photoFile,
      manualReading: parseFloat(manualReading),
    });
  };

  return (
    <div className="att-modal-overlay" onClick={onClose}>
      <div className="att-modal" onClick={(e) => e.stopPropagation()}>
        <div className="att-modal-header">
          <h3 className="att-modal-title">{isCheckIn ? "Check In" : "Check Out"}</h3>
          <button className="att-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="att-location-block">
          <div className="att-location-label">Location *</div>
          {locating && (
            <div className="att-location-status att-loc-loading">Fetching your current location…</div>
          )}
          {!locating && coords && (
            <div className="att-location-status att-loc-ok">
              📍 {coords.latitude.toFixed(5)}, {coords.longitude.toFixed(5)}
            </div>
          )}
          {!locating && locationError && (
            <div className="att-location-status att-loc-error">
              {locationError}
              <button className="att-btn-ghost att-btn-sm" style={{ marginLeft: 8 }} onClick={captureLocation}>
                Retry
              </button>
            </div>
          )}
        </div>

        <div className="att-form-group">
          <label>Address *</label>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder={locating ? "Waiting for location…" : "e.g. MG Road, Nagpur"}
          />
          {!locating && coords && !address && (
            <span className="att-field-hint">
              Couldn't auto-fill this from your location — please type it in.
            </span>
          )}
        </div>

        <div className="att-form-group">
          <label>Bike reading photo *</label>
          <label className="att-photo-upload">
            {photoPreview ? (
              <img src={photoPreview} alt="Bike reading" className="att-photo-preview" />
            ) : (
              <span>Tap to capture or upload photo</span>
            )}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: "none" }}
              onChange={handlePhotoChange}
            />
          </label>
        </div>

        <div className="att-form-group">
          <label>Bike reading (km) *</label>
          <input
            type="number"
            value={manualReading}
            onChange={(e) => setManualReading(e.target.value)}
            placeholder="e.g. 24350.5"
            min="0"
            step="0.1"
          />
        </div>

        <div className="att-modal-actions">
          <button className="att-btn-primary" style={{ flex: 1 }} onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? "Submitting…" : isCheckIn ? "Confirm Check In" : "Confirm Check Out"}
          </button>
          <button className="att-btn-ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Self: Check-in / Check-out card ─────────────────────────────────────
function CheckInOutCard({ engineerId }) {
  const { token } = getStoredUser();
  const [flowStatus, setFlowStatus] = useState("not_checked_in"); // not_checked_in | checked_in | checked_out
  const [attendance, setAttendance] = useState(null);
  const [modalMode, setModalMode] = useState(null); // "checkin" | "checkout" | null
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!engineerId) return;
    const saved = readFlow(engineerId, todayISO());
    if (saved) {
      setFlowStatus(saved.status);
      setAttendance(saved.data);
    }
  }, [engineerId]);

  async function handleCheckIn(payload) {
    setSubmitting(true);
    try {
      const dto = {
        latitude: payload.latitude,
        longitude: payload.longitude,
        address: payload.address,
        manualBikeReading: payload.manualReading,
      };
      console.log("Check-in payload (engineerAttendanceRequest):", dto);
      const data = new FormData();
      data.append("engineerAttendanceRequest", new Blob([JSON.stringify(dto)], { type: "application/json" }));
      data.append("bikeReadingPhoto", payload.photoFile);

      const response = await axiosInstance.post(`${BASE_URL}/engineer/check-in`, data, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": undefined },
      });

      setAttendance(response.data);
      setFlowStatus("checked_in");
      writeFlow(engineerId, todayISO(), { status: "checked_in", data: response.data });
      setModalMode(null);
    } catch (error) {
      console.error("Check-in failed:", error?.response?.data || error);
      alert(extractErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCheckOut(payload) {
    setSubmitting(true);
    try {
      const dto = {
        latitude: payload.latitude,
        longitude: payload.longitude,
        address: payload.address,
        manualBikeReading: payload.manualReading,
      };
      console.log("Check-out payload (checkOutRequest):", dto);
      const data = new FormData();
      data.append("checkOutRequest", new Blob([JSON.stringify(dto)], { type: "application/json" }));
      data.append("bikeReadingPhoto", payload.photoFile);

      const response = await axiosInstance.post(`${BASE_URL}/engineer/check-out`, data, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": undefined },
      });

      setAttendance(response.data);
      setFlowStatus("checked_out");
      writeFlow(engineerId, todayISO(), { status: "checked_out", data: response.data });
      setModalMode(null);
    } catch (error) {
      console.error("Check-out failed:", error?.response?.data || error);
      alert(extractErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="att-checkcard">
      <div className="att-checkcard-header">
        <div>
          <div className="att-checkcard-title">Today's Attendance</div>
          <div className="att-checkcard-sub">
            {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
          </div>
        </div>
        {attendance?.attendanceStatus && <StatusBadge status={attendance.attendanceStatus} />}
      </div>

      <div className="att-checkcard-body">
        <div className="att-time-row">
          <div className="att-time-block">
            <div className="att-time-label">Check In</div>
            <div className="att-time-value">{fmtTime(attendance?.checkInTime)}</div>
            {attendance?.checkInAddress && <div className="att-time-loc">📍 {attendance.checkInAddress}</div>}
          </div>
          <div className="att-time-block">
            <div className="att-time-label">Check Out</div>
            <div className="att-time-value">{fmtTime(attendance?.checkOutTime)}</div>
            {attendance?.checkOutAddress && <div className="att-time-loc">📍 {attendance.checkOutAddress}</div>}
          </div>
          <div className="att-time-block">
            <div className="att-time-label">Worked</div>
            <div className="att-time-value">{attendance?.workedTime || "—"}</div>
            {attendance?.locationStatus && <LocationBadge status={attendance.locationStatus} />}
          </div>
        </div>

        <div className="att-checkcard-actions">
          {flowStatus === "not_checked_in" && (
            <button className="att-btn-primary" onClick={() => setModalMode("checkin")}>Check In</button>
          )}
          {flowStatus === "checked_in" && (
            <button className="att-btn-checkout" onClick={() => setModalMode("checkout")}>Check Out</button>
          )}
          {flowStatus === "checked_out" && (
            <div className="att-done-msg">✓ Attendance completed for today</div>
          )}
        </div>
      </div>

      {modalMode && (
        <AttendanceCaptureModal
          mode={modalMode}
          onClose={() => !submitting && setModalMode(null)}
          onSubmit={modalMode === "checkin" ? handleCheckIn : handleCheckOut}
          submitting={submitting}
        />
      )}
    </div>
  );
}

// ─── Self: monthly attendance ────────────────────────────────────────────
function MyMonthlyAttendance({ engineerId }) {
  const { token } = getStoredUser();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!engineerId) return;
    setLoading(true);
    try {
      const response = await axiosInstance.get(`${BASE_URL}/engineer/${engineerId}/attendance`, {
        params: { month, year },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data);
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.message || "Failed to load monthly attendance");
    } finally {
      setLoading(false);
    }
  }, [engineerId, month, year, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="att-table-card">
      <div className="att-table-card-header">
        <span className="att-table-title">My Monthly Attendance</span>
        <div className="att-month-picker">
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {monthNames.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="att-year-input"
          />
        </div>
      </div>

      {loading ? (
        <div className="att-empty">Loading attendance…</div>
      ) : !data ? (
        <div className="att-empty">No attendance data found.</div>
      ) : (
        <>
          <div className="att-summary-row">
            <div className="att-summary-pill"><span>{data.attendanceMarkedDay}</span>Marked</div>
            <div className="att-summary-pill att-pill-green"><span>{data.presentDay}</span>Present</div>
            <div className="att-summary-pill att-pill-red"><span>{data.leaves}</span>Leaves</div>
          </div>
          <div className="att-day-grid">
            {(data.attendance || []).map((d) => (
              <div key={d.date} className="att-day-cell">
                <div className="att-day-date">{new Date(d.date).getDate()}</div>
                <div className="att-day-name">{d.day?.slice(0, 3)}</div>
                <StatusBadge status={d.attendanceStatus} small />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Admin: daily attendance dashboard ───────────────────────────────────
function DailyAttendanceDashboard() {
  const { token } = getStoredUser();
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`${BASE_URL}/engineer/attendance`, {
        params: { date },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data);
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.message || "Failed to load daily attendance");
    } finally {
      setLoading(false);
    }
  }, [date, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="att-table-card">
      <div className="att-table-card-header">
        <span className="att-table-title">Daily Attendance</span>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="att-date-input" />
      </div>

      {loading ? (
        <div className="att-empty">Loading…</div>
      ) : !data ? (
        <div className="att-empty">No data for this date.</div>
      ) : (
        <>
          <div className="att-stats att-stats-5">
            <div className="att-stat-card">
              <div className="att-stat-label">Total</div>
              <div className="att-stat-value">{data.totalEngineers}</div>
            </div>
            <div className="att-stat-card">
              <div className="att-stat-label">Present</div>
              <div className="att-stat-value att-stat-green">{data.totalPresent}</div>
            </div>
            <div className="att-stat-card">
              <div className="att-stat-label">Late</div>
              <div className="att-stat-value att-stat-amber">{data.totalLate}</div>
            </div>
            <div className="att-stat-card">
              <div className="att-stat-label">Half Day</div>
              <div className="att-stat-value att-stat-amber">{data.totalHalfDay}</div>
            </div>
            <div className="att-stat-card">
              <div className="att-stat-label">Absent</div>
              <div className="att-stat-value att-stat-red">{data.totalAbsent}</div>
            </div>
          </div>

          {(data.attendanceRecords || []).length === 0 ? (
            <div className="att-empty">No engineers recorded for this date.</div>
          ) : (
            <table className="att-table">
              <thead>
                <tr>
                  <th>Engineer</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th>Worked</th>
                  <th>Location</th>
                  <th>Photos</th>
                </tr>
              </thead>
              <tbody>
                {data.attendanceRecords.map((r) => (
                  <tr key={r.engineerId}>
                    <td>{r.engineerName}</td>
                    <td className="att-muted">{r.engineerPhoneNo}</td>
                    <td><StatusBadge status={r.attendanceStatus} /></td>
                    <td>{fmtTime(r.checkInTime)}</td>
                    <td>{fmtTime(r.checkOutTime)}</td>
                    <td>{r.workedHours || "—"}</td>
                    <td><LocationBadge status={r.locationStatus} /></td>
                    <td>
                      <div className="att-photo-links">
                        {r.bikeReadingCheckInPhoto && (
                          <a href={resolvePhotoUrl(r.bikeReadingCheckInPhoto)} target="_blank" rel="noreferrer">In</a>
                        )}
                        {r.bikeReadingCheckOutPhoto && (
                          <a href={resolvePhotoUrl(r.bikeReadingCheckOutPhoto)} target="_blank" rel="noreferrer">Out</a>
                        )}
                        {!r.bikeReadingCheckInPhoto && !r.bikeReadingCheckOutPhoto && "—"}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

// ─── Admin: monthly attendance report ────────────────────────────────────
function MonthlyAttendanceReport() {
  const { token } = getStoredUser();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`${BASE_URL}/engineer/monthly/attendance-report`, {
        params: { month, year },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data || []);
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.message || "Failed to load monthly report");
    } finally {
      setLoading(false);
    }
  }, [month, year, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="att-table-card">
      <div className="att-table-card-header">
        <span className="att-table-title">Monthly Attendance Report</span>
        <div className="att-month-picker">
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {monthNames.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="att-year-input"
          />
        </div>
      </div>

      {loading ? (
        <div className="att-empty">Loading…</div>
      ) : data.length === 0 ? (
        <div className="att-empty">No records found.</div>
      ) : (
        <table className="att-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Designation</th>
              <th>Marked</th>
              <th>Present</th>
              <th>Leaves</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.map((emp) => (
              <React.Fragment key={emp.employeeId}>
                <tr>
                  <td>{emp.employeeName}</td>
                  <td className="att-muted">{emp.designation || "—"}</td>
                  <td>{emp.attendanceMarkedDay}</td>
                  <td className="att-stat-green">{emp.presentDay}</td>
                  <td>{emp.leaves}</td>
                  <td>
                    <button
                      className="att-btn-ghost att-btn-sm"
                      onClick={() => setExpandedId(expandedId === emp.employeeId ? null : emp.employeeId)}
                    >
                      {expandedId === emp.employeeId ? "Hide days" : "View days"}
                    </button>
                  </td>
                </tr>
                {expandedId === emp.employeeId && (
                  <tr>
                    <td colSpan={6}>
                      <div className="att-day-grid att-day-grid-nested">
                        {(emp.attendance || []).map((d) => (
                          <div key={d.date} className="att-day-cell">
                            <div className="att-day-date">{new Date(d.date).getDate()}</div>
                            <div className="att-day-name">{d.day?.slice(0, 3)}</div>
                            <StatusBadge status={d.attendanceStatus} small />
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────
function EngineerAttendance() {
  const user = getStoredUser();
  const roleNames = getRoleNames(user);
  const isEngineer = roleNames.includes("ENGINEER");
  const isAdmin = roleNames.some((r) =>
    ["ADMIN", "SUPERENGINEER", "SUPER_ENGINEER", "SUPER ENGINEER"].includes(r)
  );
  const engineerId = user.engineerId || user.id;

  const [activeTab, setActiveTab] = useState(isEngineer ? "self" : "team");

  return (
    <div className="att-page">
      <div className="att-topbar">
        <div>
          <h2 className="att-page-title">Attendance</h2>
          <p className="att-page-sub">
            {isEngineer ? "Check in, check out, and track your monthly attendance" : "Monitor engineer attendance across your team"}
          </p>
        </div>
      </div>

      {isEngineer && isAdmin && (
        <div className="att-tabs">
          <button
            className={`att-tab ${activeTab === "self" ? "active" : ""}`}
            onClick={() => setActiveTab("self")}
          >
            My Attendance
          </button>
          <button
            className={`att-tab ${activeTab === "team" ? "active" : ""}`}
            onClick={() => setActiveTab("team")}
          >
            Team Attendance
          </button>
        </div>
      )}

      {isEngineer && (!isAdmin || activeTab === "self") && (
        <>
          <CheckInOutCard engineerId={engineerId} />
          <MyMonthlyAttendance engineerId={engineerId} />
        </>
      )}

      {isAdmin && (!isEngineer || activeTab === "team") && (
        <>
          <DailyAttendanceDashboard />
          <MonthlyAttendanceReport />
        </>
      )}

      {!isEngineer && !isAdmin && (
        <div className="att-empty">Your account role doesn't have access to attendance data.</div>
      )}
    </div>
  );
}

export default EngineerAttendance;